import type { EditorialStep } from 'ui/molecules/EditorialStepsSection/EditorialStepsSection.types';
import type { NumberedFeatureCard } from 'ui/organisms/NumberedFeatureCards/NumberedFeatureCards.types';

import type { AttributionStageData } from 'features/crm/organisms/AttributionStage/AttributionStage.types';
import type { CrmApp } from 'features/crm/organisms/CrmAppRows/CrmAppRows.types';
import type { EnrichRecord } from 'features/crm/organisms/CrmEnrichDemo/CrmEnrichDemo.types';
import type { DeliveryChannel } from 'features/crm/organisms/DeliveryChannels/DeliveryChannels.types';

// ── Hero demo card ──
export const DEMO_RECORD: EnrichRecord = {
  companyName: 'Revolut Ltd',
  companyMeta: 'Account · London, United Kingdom',
  badge: 'Enriched',
  syncsTo: ['Salesforce', 'HubSpot', 'Dynamics 365'],
  fields: [
    { label: 'Company number', value: '08804411', source: 'Companies House' },
    { label: 'VAT number', value: 'GB 245 0117 87', source: 'HMRC registry' },
    { label: 'Legal form', value: 'Private Limited Company', source: 'Companies House' },
    { label: 'Revenue (FY24)', value: '£3.1bn', source: 'Filed accounts · AA-2024' },
  ],
};

// ── CRM apps ──
export const CRM_APPS: CrmApp[] = [
  {
    key: 'salesforce',
    name: 'Salesforce',
    iconType: 'crm-1',
    body: 'Enrich Accounts and Leads from the record page or across a list view. Registration numbers give your matching rules an official identifier to dedupe on, and group structure from filings gives the Parent Account hierarchy something real to build from.',
    cta: {
      label: 'Install on AppExchange',
      href: 'https://appexchange.salesforce.com/appxListingDetail?listingId=7d8cb601-add2-47f7-a345-4008cbb0cfe4',
      external: true,
    },
  },
  {
    key: 'hubspot',
    name: 'HubSpot',
    iconType: 'crm-2',
    body: 'Enrich company and contact records from a card on the record page, or run lists in bulk. Global Database fields map to your HubSpot properties — fill empty fields only if you choose — and enriched properties feed your active lists and workflows straight away.',
    cta: {
      label: 'Install from Marketplace',
      href: 'https://ecosystem.hubspot.com/marketplace/listing/global-database-intelligence-enrichment',
      external: true,
    },
  },
  {
    key: 'dynamics',
    name: 'Microsoft Dynamics 365',
    iconType: 'crm-3',
    body: 'Enrich Accounts with registry-held legal names, registration numbers and VAT — the fields finance depends on when the account flows into your ERP. Our engineers deploy the integration against your environment and map fields to your entities.',
    cta: {
      label: 'Book a Dynamics demo',
      href: 'https://www.globaldatabase.com/book-a-demo',
    },
  },
];

// ── Five data blocks ──
export const DATA_BLOCKS: EditorialStep[] = [
  {
    num: '01',
    title: 'Company identifiers',
    description:
      'Registration number, VAT, LEI, EIN and DUNS resolved to one profile. These are the match keys — they merge duplicates, join the CRM to the ERP, and prove an entity legally exists.',
  },
  {
    num: '02',
    title: 'Group structure & ownership',
    description:
      'Parents, subsidiaries and ultimate beneficial owners from official filings. Territories, key-account plans and credit exposure calculated on the real corporate tree — the subsidiary a rep is prospecting connects to the parent another rep already closed.',
  },
  {
    num: '03',
    title: 'Firmographics',
    description:
      'Industry, SIC and NAICS codes, employee counts, size bands and locations. The fields routing, scoring and segmentation rules actually fire on — verified at the source, so the rules fire correctly.',
  },
  {
    num: '04',
    title: 'Financials',
    description:
      'Up to five years of filed accounts — revenue, EBITDA, net profit, liabilities, net worth. Qualification and credit decisions made on numbers the company filed with its registry.',
  },
  {
    num: '05',
    title: 'Contacts',
    description:
      'Decision-makers matched to the enriched company — name, role and business email — so outreach starts the moment the record is complete.',
  },
];

// ── Attribution stage ──
export const ATTRIBUTION: AttributionStageData = {
  source: {
    label: 'Official register',
    register: 'Companies House',
    filing: 'Annual accounts · AA-2024',
    filingHref: 'https://find-and-update.company-information.service.gov.uk/company/08804411/filing-history',
    retrieved: 'Retrieved 14 Jul 2026',
  },
  crm: {
    destination: 'Account.AnnualRevenue',
    fieldLabel: 'Annual revenue — FY 2024',
    fieldValue: '£3.1bn',
    verify: 'Verified against the original filing',
  },
};

// ── How teams run it ──
export const RUN_STEPS: NumberedFeatureCard[] = [
  {
    num: '01',
    title: 'Enrich what you have',
    description:
      'Run your whole database through in one go. Missing fields get filled, duplicates are matched by their registration numbers, and every account comes out complete and correct.',
  },
  {
    num: '02',
    title: 'Enrich new records as they arrive',
    description:
      "Every new lead or account is enriched automatically the moment it's created. By the time someone opens the record, the company details are already there.",
  },
  {
    num: '03',
    title: 'Keep everything up to date',
    description:
      'Records stay current after that. When a registry publishes something new — a filing, a status change, a new owner — the record in your CRM updates with it.',
  },
];

// ── Use cases ──
export const USE_CASES: EditorialStep[] = [
  {
    num: '01',
    title: 'Lead routing',
    iconType: 'gps',
    description:
      'A lead from a 4,000-person manufacturer stops landing in the SMB queue, because the employee count came from a filing rather than a form field.',
  },
  {
    num: '02',
    title: 'Territory planning',
    iconType: 'earth',
    description:
      'Every subsidiary connected to its parent, so quotas and named accounts are built on the corporate tree rather than a flat list of logos.',
  },
  {
    num: '03',
    title: 'Renewals',
    iconType: 'refresh',
    description:
      'Refresh the account before the renewal call — a status change, a new owner or a fresh set of accounts is on the record before the conversation starts.',
  },
  {
    num: '04',
    title: 'Credit decisions',
    iconType: 'payg-account-scoring',
    description:
      'Filed revenue and net worth sit on the record, and group structure shows what you are exposed to across the whole family — not just the entity on the invoice.',
  },
  {
    num: '05',
    title: 'Onboarding',
    iconType: 'check-2',
    description: 'The legal name and registration number match the register from day one.',
  },
  {
    num: '06',
    title: 'Segmentation',
    iconType: 'payg-industry-classification',
    description:
      'Segments built on official industry codes and size bands, with decision-maker contacts already attached.',
  },
];

// ── Also delivered through ──
export const DELIVERY_CHANNELS: DeliveryChannel[] = [
  {
    key: 'api',
    name: 'REST API',
    iconType: 'payg-technographic-data',
    linkLabel: 'View the API docs',
    href: 'https://www.globaldatabase.com/api',
  },
  {
    key: 'claude',
    name: 'Add to Claude',
    iconType: 'payg-company-insights',
    linkLabel: 'Install free',
    href: 'https://www.globaldatabase.com/global-database-is-now-live-on-claude-free-to-install',
  },
  {
    key: 'chatgpt',
    name: 'Add to ChatGPT',
    iconType: 'payg-intent-signals',
    linkLabel: 'Install free',
    href: 'https://www.globaldatabase.com/mcp',
  },
];
